import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import mammoth from 'mammoth';
import DOMPurify from 'dompurify';
import { getSubmissions, getIssues, fetchFileBlob, createIssue, toggleIssue, bulkStatusUpdate, updateIssue, getAuthors, uploadFile, adminCreateSubmission, getSections, deleteSubmission, downloadCertificates } from '../api/client';
import { StatusBadge, PaymentBadge } from '../components/StatusBadge';
import Modal from '../components/Modal';
import AuthorPickerModal from '../components/AuthorPickerModal';
import { useAuth } from '../App';

// Status priority for default sort: active statuses come first (need attention),
// then terminal statuses (accepted/rejected/published) drop to the bottom.
const STATUS_SORT_WEIGHT = {
  submitted: 1,
  under_review: 2,
  revision_required: 3,
  accepted: 4,
  published: 5,
  rejected: 6,
};

const STATUS_OPTIONS = [
  { value: '', label: 'Barchasi' },
  { value: 'submitted', label: 'Ko\'rib chiqilmoqda' },
  { value: 'under_review', label: 'Taqrizda' },
  { value: 'revision_required', label: 'Qayta ishlash' },
  { value: 'accepted', label: 'Qabul qilingan' },
  { value: 'rejected', label: 'Rad etilgan' },
  { value: 'published', label: 'Chop etilgan' },
];

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const getFileType = (filename) => {
  if (!filename) return 'unknown';
  const ext = filename.split('.').pop().toLowerCase();
  if (['pdf'].includes(ext)) return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
  if (['doc', 'docx'].includes(ext)) return 'docx';
  if (['tex'].includes(ext)) return 'tex';
  return 'unknown';
};

export default function SubmissionsPage() {
  const { selectedJournalSlug, currentJournal } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ year: new Date().getFullYear(), number: '1' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showEditIssueModal, setShowEditIssueModal] = useState(false);
  const [editIssueForm, setEditIssueForm] = useState({});

  // ── Manual article creation (admin) ──
  const emptyArticleForm = {
    // selectedAuthor shape: { mode: 'existing'|'new', id?, author? }
    selectedAuthor: null,
    language: 'uz',
    section: '',
    sectionCustom: false,  // true when the "boshqa…" option is picked
    title: '', title_en: '', title_ru: '',
    abstract: '', abstract_en: '', abstract_ru: '',
    keywords: '', keywords_en: '', keywords_ru: '',
    references_list: '',
    file_path: '', file_name: '',
    status: 'submitted',
    coauthors: [],  // array of { mode, id?, author? }
  };
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [articleForm, setArticleForm] = useState(emptyArticleForm);
  const [articleAuthors, setArticleAuthors] = useState([]);
  const [articleSubmitting, setArticleSubmitting] = useState(false);
  const [articleUploading, setArticleUploading] = useState(false);
  const [sections, setSections] = useState([]);
  // Author picker modal state: { role: 'author'|'coauthor', editIndex?: number }
  const [authorPicker, setAuthorPicker] = useState(null);

  // Pagination & Sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    return () => {
      if (previewFile?.objectUrl) {
        URL.revokeObjectURL(previewFile.objectUrl);
      }
    };
  }, [previewFile]);

  useEffect(() => {
    setSelectedIssue(null);
    setSubmissions([]);
    setStatusFilter('');
    setSelectedIds(new Set());
    setLoading(true);
    const fetchData = async () => {
      try {
        const issuesRes = await getIssues();
        setIssues(issuesRes.data);
        const issueId = searchParams.get('issue');
        if (issueId) {
          const found = issuesRes.data.find(i => i.id === parseInt(issueId));
          if (found) setSelectedIssue(found);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedJournalSlug]); // eslint-disable-line

  useEffect(() => {
    if (!selectedIssue) return;
    const fetchSubs = async () => {
      setLoading(true);
      try {
        const params = { issue_id: selectedIssue.id };
        if (statusFilter) params.status = statusFilter;
        if (searchQuery.trim()) params.q = searchQuery.trim();
        const res = await getSubmissions(params);
        setSubmissions(res.data);
        setCurrentPage(1);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    // Debounce searchQuery
    const t = setTimeout(fetchSubs, searchQuery ? 350 : 0);
    return () => clearTimeout(t);
  }, [selectedIssue, statusFilter, selectedJournalSlug, searchQuery]);

  const selectIssue = (issue) => {
    setSelectedIssue(issue);
    setSearchParams({ issue: issue.id });
  };

  const goBackToIssues = () => {
    setSelectedIssue(null);
    setSubmissions([]);
    setStatusFilter('');
    setSearchParams({});
  };

  const handleCreateIssue = async (e) => {
    e.preventDefault();
    const yr = parseInt(createForm.year);
    const num = parseInt(createForm.number);
    const title = `${yr}-yil ${num}-son`;
    try {
      await createIssue({
        title,
        volume: String(yr - 2019),
        number: String(num),
        year: yr,
      });
      toast.success('Son yaratildi!');
      setShowCreateModal(false);
      setCreateForm({ year: new Date().getFullYear(), number: '1' });
      const res = await getIssues();
      setIssues(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Xatolik');
    }
  };

  const handleToggleIssue = async (issueId, e) => {
    e.stopPropagation();
    try {
      await toggleIssue(issueId);
      toast.success("Status o'zgartirildi");
      const res = await getIssues();
      setIssues(res.data);
    } catch (err) {
      toast.error('Xatolik');
    }
  };

  // ── Bulk Actions ──
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === submissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(submissions.map(s => s.id)));
    }
  };
  const handleBulkStatus = async (newStatus) => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size} ta maqola statusini "${newStatus}" ga o'zgartirmoqchimisiz?`)) return;
    try {
      await bulkStatusUpdate({ submission_ids: [...selectedIds], status: newStatus });
      toast.success(`${selectedIds.size} ta maqola yangilandi`);
      setSelectedIds(new Set());
      const params = { issue_id: selectedIssue.id };
      if (statusFilter) params.status = statusFilter;
      const res = await getSubmissions(params);
      setSubmissions(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Xatolik');
    }
  };

  // ── Quick certificate download from list row ──
  const handleQuickCertDownload = async (sub) => {
    const kind = sub.status === 'published' ? 'published' : 'accepted';
    const dismiss = toast.loading('Sertifikatlar tayyorlanmoqda…');
    try {
      const res = await downloadCertificates(sub.id, kind);
      const cd = res.headers?.['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const filename = (m && m[1]) || `Certificates_Sub${sub.id}_${kind}.zip`;
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Sertifikatlar yuklab olindi", { id: dismiss });
    } catch (err) {
      let detail = "Sertifikat tayyorlashda xatolik";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try { detail = JSON.parse(await data.text())?.detail || detail; } catch (_) { /* keep */ }
      } else if (data?.detail) {
        detail = data.detail;
      }
      toast.error(detail, { id: dismiss });
    }
  };

  // ── Delete a single submission (errored / duplicate) ──
  const handleDeleteSubmission = async (sub) => {
    const label = sub.title ? `"${sub.title}"` : `#${sub.id}`;
    if (!window.confirm(
      `Diqqat! ${label} maqolasini bir umrga o'chirmoqchimisiz?\n\n` +
      `Bu amalni qaytarib bo'lmaydi: maqola, taqrizlar va hammualliflar yo'qoladi.`
    )) return;
    try {
      await deleteSubmission(sub.id);
      toast.success(`Maqola #${sub.id} o'chirildi`);
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(sub.id);
        return next;
      });
      const params = { issue_id: selectedIssue.id };
      if (statusFilter) params.status = statusFilter;
      const res = await getSubmissions(params);
      setSubmissions(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "O'chirishda xatolik");
    }
  };

  // ── Issue Edit ──
  const openEditIssue = (issue) => {
    setEditIssueForm({ title: issue.title, volume: issue.volume || '', number: issue.number || '', year: issue.year });
    setShowEditIssueModal(true);
  };
  const handleEditIssue = async (e) => {
    e.preventDefault();
    try {
      await updateIssue(selectedIssue.id, editIssueForm);
      toast.success("Son yangilandi");
      setShowEditIssueModal(false);
      const res = await getIssues();
      setIssues(res.data);
      const updated = res.data.find(i => i.id === selectedIssue.id);
      if (updated) setSelectedIssue(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Xatolik');
    }
  };

  // ── Manual article (admin) handlers ──
  const openArticleModal = async () => {
    setArticleForm({ ...emptyArticleForm, language: 'uz', status: 'submitted' });
    setShowArticleModal(true);
    const [authorsRes, sectionsRes] = await Promise.allSettled([
      getAuthors(),
      getSections(),
    ]);
    if (authorsRes.status === 'fulfilled') {
      setArticleAuthors(Array.isArray(authorsRes.value.data) ? authorsRes.value.data : []);
    } else {
      console.warn('Failed to load authors', authorsRes.reason);
      setArticleAuthors([]);
    }
    if (sectionsRes.status === 'fulfilled') {
      setSections(sectionsRes.value.data?.sections || []);
    } else {
      console.warn('Failed to load sections', sectionsRes.reason);
      setSections([]);
    }
  };
  const setArticleField = (field, value) => setArticleForm(prev => ({ ...prev, [field]: value }));

  const handleAuthorPicked = (pick) => {
    if (!pick) return;
    if (pick.role === 'coauthor') {
      setArticleForm(prev => ({
        ...prev,
        coauthors: [...prev.coauthors, pick],
      }));
    } else {
      setArticleForm(prev => ({ ...prev, selectedAuthor: pick }));
    }
  };
  const removeCoauthor = (idx) =>
    setArticleForm(prev => ({ ...prev, coauthors: prev.coauthors.filter((_, i) => i !== idx) }));

  // Label helper — handles both {mode:'existing', author:{...}} and {mode:'new', author:{...}}
  const authorLabel = (pick) => {
    if (!pick || !pick.author) return '—';
    const a = pick.author;
    const name = `${a.last_name || ''} ${a.first_name || ''}${a.middle_name ? ` ${a.middle_name}` : ''}`.trim();
    const tag = pick.mode === 'existing' ? ' (bazadan)' : ' (yangi)';
    const extra = a.email ? ` · ${a.email}` : '';
    return `${name}${tag}${extra}`;
  };

  const handleArticleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setArticleUploading(true);
    try {
      const res = await uploadFile(file);
      setArticleForm(prev => ({
        ...prev,
        file_path: res.data.file_path,
        file_name: res.data.file_name || file.name,
      }));
      toast.success(`Fayl yuklandi: ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Faylni yuklab bo\'lmadi');
    } finally {
      setArticleUploading(false);
    }
  };

  const handleArticleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedIssue) return;

    const required = ['language', 'section', 'title', 'title_en', 'abstract', 'abstract_en', 'keywords', 'keywords_en', 'file_path', 'file_name'];
    for (const f of required) {
      if (!articleForm[f] || String(articleForm[f]).trim() === '') {
        toast.error(`Majburiy maydon bo'sh: ${f}`);
        return;
      }
    }
    const picked = articleForm.selectedAuthor;
    if (!picked) {
      toast.error('Asosiy muallifni qo\'shing');
      return;
    }

    const payload = {
      issue_id: selectedIssue.id,
      language: articleForm.language,
      section: articleForm.section,
      title: articleForm.title,
      title_en: articleForm.title_en,
      title_ru: articleForm.title_ru || null,
      abstract: articleForm.abstract,
      abstract_en: articleForm.abstract_en,
      abstract_ru: articleForm.abstract_ru || null,
      keywords: articleForm.keywords,
      keywords_en: articleForm.keywords_en,
      keywords_ru: articleForm.keywords_ru || null,
      references_list: articleForm.references_list || null,
      file_path: articleForm.file_path,
      file_name: articleForm.file_name,
      status: articleForm.status || 'submitted',
      // Each coauthor pick is flattened to the backend's CoAuthorCreate shape.
      // For 'existing' picks we MUST forward the source Author.id as `author_id`
      // so the new coauthors row links back to the canonical Author record
      // (otherwise the FK stays NULL and we get an orphan duplicate).
      coauthors: articleForm.coauthors
        .filter(c => c && c.author && c.author.first_name && c.author.last_name)
        .map(c => {
          const a = c.author;
          const base = {
            first_name: a.first_name,
            last_name: a.last_name,
            middle_name: a.middle_name || null,
            first_name_en: a.first_name_en || null,
            last_name_en: a.last_name_en || null,
            middle_name_en: a.middle_name_en || null,
            first_name_ru: a.first_name_ru || null,
            last_name_ru: a.last_name_ru || null,
            middle_name_ru: a.middle_name_ru || null,
            email: a.email || null,
            phone: a.phone || null,
            orcid: a.orcid || null,
            country: a.country || null,
            affiliation: a.affiliation || null,
            affiliation_en: a.affiliation_en || null,
            affiliation_ru: a.affiliation_ru || null,
          };
          if (c.mode === 'existing' && (c.id || a.id)) {
            base.author_id = c.id || a.id;
          }
          return base;
        }),
    };
    if (picked.mode === 'existing') {
      payload.existing_author_id = picked.id;
    } else {
      payload.new_author = picked.author;
    }

    setArticleSubmitting(true);
    try {
      await adminCreateSubmission(payload);
      toast.success('Maqola yaratildi');
      setShowArticleModal(false);
      setArticleForm(emptyArticleForm);
      // Refresh submission list
      const params = { issue_id: selectedIssue.id };
      if (statusFilter) params.status = statusFilter;
      const res = await getSubmissions(params);
      setSubmissions(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Maqola yaratishda xatolik');
    } finally {
      setArticleSubmitting(false);
    }
  };

  const issuesByYear = issues.reduce((acc, issue) => {
    const year = issue.year || 'Boshqa';
    if (!acc[year]) acc[year] = [];
    acc[year].push(issue);
    return acc;
  }, {});
  Object.keys(issuesByYear).forEach(year => {
    issuesByYear[year].sort((a, b) => (parseInt(a.number) || 0) - (parseInt(b.number) || 0));
  });
  const sortedYears = Object.keys(issuesByYear).sort((a, b) => b - a);

  const formatAuthors = (sub) => {
    if (!sub.author) return '-';
    const firstInitial = sub.author.first_name?.[0] || '';
    const mainAuthor = `${firstInitial}.${sub.author.last_name}`;
    if (sub.coauthors && sub.coauthors.length > 0) {
      return `${mainAuthor}, et al.`;
    }
    return mainAuthor;
  };

  // Sorting logic
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const sortedSubmissions = React.useMemo(() => {
    // Default (no explicit sort field chosen): group by status priority so
    // active submissions (need editor attention) bubble to the top and
    // accepted/rejected/published drop to the bottom. Within each status
    // group, newest-first.
    if (!sortField) {
      return [...submissions].sort((a, b) => {
        const wA = STATUS_SORT_WEIGHT[a.status] ?? 99;
        const wB = STATUS_SORT_WEIGHT[b.status] ?? 99;
        if (wA !== wB) return wA - wB;
        // Tie-break: newer submission first
        const tA = a.submitted_at ? new Date(a.submitted_at).getTime() : 0;
        const tB = b.submitted_at ? new Date(b.submitted_at).getTime() : 0;
        return tB - tA;
      });
    }
    const sorted = [...submissions].sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case 'title': valA = a.title || ''; valB = b.title || ''; break;
        case 'section': valA = a.section || ''; valB = b.section || ''; break;
        case 'author': valA = a.author?.last_name || ''; valB = b.author?.last_name || ''; break;
        case 'date': valA = a.submitted_at || ''; valB = b.submitted_at || ''; break;
        case 'status':
          // When sorting by status, honor the same priority order rather than
          // alphabetical — the user expects "active first" semantics.
          valA = STATUS_SORT_WEIGHT[a.status] ?? 99;
          valB = STATUS_SORT_WEIGHT[b.status] ?? 99;
          break;
        default: return 0;
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [submissions, sortField, sortDirection]);

  // Pagination logic
  const totalPages = Math.ceil(sortedSubmissions.length / itemsPerPage);
  const paginatedSubmissions = sortedSubmissions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="text-slate-300 ml-1">↕</span>;
    return <span className="text-blue-600 ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const getStoredFileName = (path) => {
    if (!path) return null;
    return path;
  };

  const closePreview = () => {
    setPreviewFile((prev) => {
      if (prev?.objectUrl) {
        URL.revokeObjectURL(prev.objectUrl);
      }
      return null;
    });
  };

  const openPreview = async (storedName, originalName) => {
    if (!storedName) return;
    const type = getFileType(storedName);
    try {
      const res = await fetchFileBlob(storedName, type !== 'docx');
      const objectUrl = URL.createObjectURL(res.data);
      let docxHtml = null;
      if (type === 'docx') {
        const arrayBuffer = await res.data.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        docxHtml = DOMPurify.sanitize(result.value, {
          ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 'ul', 'ol', 'li',
                          'h1', 'h2', 'h3', 'h4', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'span'],
          ALLOWED_ATTR: []
        });
      }
      setPreviewFile((prev) => {
        if (prev?.objectUrl) {
          URL.revokeObjectURL(prev.objectUrl);
        }
        return {
          previewUrl: objectUrl,
          downloadUrl: objectUrl,
          objectUrl,
          name: originalName || storedName,
          type,
          docxHtml,
        };
      });
    } catch (err) {
      let message = "Faylni ochib bo'lmadi";
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          message = json.detail || message;
        } catch {}
      } else if (err.response?.data?.detail) {
        message = err.response.data.detail;
      }
      toast.error(message);
    }
  };

  // ── Issue selection view ──
  if (!selectedIssue) {
    if (loading) return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
      </div>
    );

    return (
      <div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Sonlar</h2>
            <p className="text-sm text-slate-400 mt-1">
              {(currentJournal?.short_name || currentJournal?.name || 'Tanlangan jurnal')} uchun sonni tanlang
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.5v15m7.5-7.5h-15" /></svg>
            Yangi son
          </button>
        </div>

        {sortedYears.map((year) => (
          <div key={year} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm font-bold text-slate-900">{year}-yil</span>
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-400 font-medium">{issuesByYear[year].length} ta son</span>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="divide-y divide-slate-100">
                {issuesByYear[year].map((issue) => {
                  const count = issue.submission_count || 0;
                  return (
                    <div key={issue.id} className="flex items-center group">
                      <button
                        onClick={() => selectIssue(issue)}
                        className="flex-1 flex items-center gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
                      >
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${issue.is_open ? 'bg-emerald-500' : 'bg-slate-300'}`}
                          title={issue.is_open ? 'Maqola qabuli ochiq' : 'Maqola qabuli yopiq'} />

                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-semibold text-slate-900">{issue.title}</span>
                          <span className="text-xs text-slate-400 ml-2">Vol.{issue.volume}, №{issue.number}</span>
                        </div>

                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <svg className="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                          <span className="text-xs font-medium text-slate-500">{count}</span>
                        </div>

                        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium flex-shrink-0 ${
                          issue.is_open ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {issue.is_open ? 'Ochiq' : 'Yopiq'}
                        </span>

                        <svg className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </button>

                      <button
                        onClick={(e) => handleToggleIssue(issue.id, e)}
                        className={`px-4 py-4 border-l border-slate-100 text-xs font-medium transition-colors flex-shrink-0 ${
                          issue.is_open ? 'text-red-500 hover:bg-red-50' : 'text-emerald-600 hover:bg-emerald-50'
                        }`}
                        title={issue.is_open ? 'Sonni yopish' : 'Sonni ochish'}
                      >
                        {issue.is_open ? 'Yopish' : 'Ochish'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}

        {issues.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg>
            </div>
            <p className="text-sm text-slate-500 mb-3">Hali sonlar yo'q</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Birinchi sonni yarating →
            </button>
          </div>
        )}

        <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Yangi son yaratish">
          <form onSubmit={handleCreateIssue} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Yil</label>
                <select
                  value={createForm.year}
                  onChange={(e) => setCreateForm({ ...createForm, year: e.target.value })}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Son raqami</label>
                <select
                  value={createForm.number}
                  onChange={(e) => setCreateForm({ ...createForm, number: e.target.value })}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {[1,2,3,4,5,6].map((n) => (
                    <option key={n} value={n}>{n}-son</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="bg-blue-50 rounded-xl p-3">
              <p className="text-xs text-blue-600 font-medium">Nomi avtomatik:</p>
              <p className="text-sm font-semibold text-blue-900 mt-0.5">{createForm.year}-yil {createForm.number}-son</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowCreateModal(false)}
                className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm hover:bg-slate-50 transition-colors">Bekor</button>
              <button type="submit"
                className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors">Yaratish</button>
            </div>
          </form>
        </Modal>
      </div>
    );
  }

  // ── Submissions list view ──
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={goBackToIssues}
            className="w-9 h-9 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div>
            <h2 className="text-lg font-bold text-slate-900">{selectedIssue.title}</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {(currentJournal?.short_name || currentJournal?.name || 'Tanlangan jurnal')} · Vol.{selectedIssue.volume}, №{selectedIssue.number}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="🔍 Sarlavha, muallif, DOI, ORCID..."
            className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none min-w-[260px]"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="text-xs text-slate-400 bg-white border border-slate-200 px-3 py-2 rounded-xl font-medium whitespace-nowrap">{submissions.length} ta</span>
          <button onClick={openArticleModal} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition-colors" title="Yangi maqola qo'shish">+ Yangi maqola</button>
          <button onClick={() => openEditIssue(selectedIssue)} className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium hover:bg-slate-50 transition-colors" title="Sonni tahrirlash">✏️ Tahrirlash</button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
        </div>
      ) : submissions.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
          </div>
          <p className="text-sm text-slate-500">Bu sonda hali maqolalar yo'q</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {selectedIds.size > 0 && (
            <div className="px-5 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-3 flex-wrap">
              <span className="text-xs font-medium text-blue-700">{selectedIds.size} ta tanlangan:</span>
              {STATUS_OPTIONS.filter(o => o.value).map(opt => (
                <button key={opt.value} onClick={() => handleBulkStatus(opt.value)} className="px-2.5 py-1 rounded-lg bg-white border border-blue-200 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors">{opt.label}</button>
              ))}
              <button onClick={() => setSelectedIds(new Set())} className="ml-auto text-xs text-slate-400 hover:text-slate-600">Bekor</button>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-3 py-3.5 w-10"><input type="checkbox" checked={selectedIds.size === submissions.length && submissions.length > 0} onChange={toggleSelectAll} className="rounded border-slate-300" /></th>
                  <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider w-12">#</th>
                  <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-slate-600" onClick={() => handleSort('title')}>Maqola nomi<SortIcon field="title" /></th>
                  <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-slate-600" onClick={() => handleSort('section')}>Yo'nalish<SortIcon field="section" /></th>
                  <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-slate-600" onClick={() => handleSort('author')}>Mualliflar<SortIcon field="author" /></th>
                  <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-slate-600" onClick={() => handleSort('date')}>Sana<SortIcon field="date" /></th>
                  <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-slate-600" onClick={() => handleSort('status')}>Status<SortIcon field="status" /></th>
                  <th className="px-5 py-3.5 text-center text-xs font-medium text-slate-400 uppercase tracking-wider w-14">Fayl</th>
                  <th className="px-5 py-3.5 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">To'lov habarnomasi</th>
                  <th className="px-5 py-3.5 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">To'lov holati</th>
                  <th className="px-5 py-3.5 text-center text-xs font-medium text-slate-400 uppercase tracking-wider w-14">Sertifikat</th>
                  <th className="px-5 py-3.5 text-center text-xs font-medium text-slate-400 uppercase tracking-wider w-14">Amal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {paginatedSubmissions.map((sub, index) => {
                  const articleFile = getStoredFileName(sub.file_path);
                  const receiptFile = getStoredFileName(sub.payment_receipt_path);
                  const globalIndex = (currentPage - 1) * itemsPerPage + index;
                  return (
                    <tr key={sub.id} className={`hover:bg-slate-50 transition-colors ${selectedIds.has(sub.id) ? 'bg-blue-50/50' : ''}`}>
                      <td className="px-3 py-3.5"><input type="checkbox" checked={selectedIds.has(sub.id)} onChange={() => toggleSelect(sub.id)} className="rounded border-slate-300" /></td>
                      <td className="px-5 py-3.5 text-sm text-slate-300 font-medium">{globalIndex + 1}</td>
                      <td className="px-5 py-3.5 text-sm max-w-xs">
                        <Link to={`/submissions/${sub.id}?issue=${selectedIssue.id}`} className="text-slate-900 hover:text-blue-600 transition-colors font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            {!sub.is_read && (
                              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 flex-shrink-0 animate-pulse" title="O'qilmagan" />
                            )}
                            <span className="line-clamp-2">{sub.title}</span>
                            {sub.source_format === 'tex' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 flex-shrink-0">LaTeX</span>
                            )}
                          </span>
                        </Link>
                      </td>
                      <td className="px-5 py-3.5">
                        {sub.section ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium">{sub.section}</span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-600 whitespace-nowrap">{formatAuthors(sub)}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-400 whitespace-nowrap">{formatDate(sub.submitted_at)}</td>
                      <td className="px-5 py-3.5"><StatusBadge status={sub.status} /></td>
                      <td className="px-5 py-3.5 text-center">
                        {articleFile ? (
                          <button
                            onClick={() => openPreview(articleFile, sub.file_name)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                            title={sub.file_name || "Faylni ko'rish"}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                          </button>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        {receiptFile ? (
                          <button
                            onClick={() => openPreview(receiptFile, sub.payment_receipt_name)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-amber-600 hover:bg-amber-50 transition-colors"
                            title={sub.payment_receipt_name || "To'lov habarnomasini ko'rish"}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </button>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <PaymentBadge status={sub.payment_status} />
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        {(sub.status === 'accepted' || sub.status === 'published') && (
                          <button
                            onClick={() => handleQuickCertDownload(sub)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors"
                            title={`Sertifikatlar ZIP (${sub.status === 'published' ? 'nashr' : 'qabul'})`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <button
                          onClick={() => handleDeleteSubmission(sub)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors"
                          title="Maqolani o'chirish (qaytarib bo'lmaydi)"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && submissions.length > 0 && (
        <div className="flex items-center justify-between mt-4 bg-white rounded-2xl border border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Sahifada:</span>
            {[20, 50, 100].map(n => (
              <button key={n} onClick={() => { setItemsPerPage(n); setCurrentPage(1); }}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${itemsPerPage === n ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{n}</button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">←</button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let page;
              if (totalPages <= 7) {
                page = i + 1;
              } else if (currentPage <= 4) {
                page = i + 1;
              } else if (currentPage >= totalPages - 3) {
                page = totalPages - 6 + i;
              } else {
                page = currentPage - 3 + i;
              }
              return (
                <button key={page} onClick={() => setCurrentPage(page)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${currentPage === page ? 'bg-blue-600 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}>{page}</button>
              );
            })}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">→</button>
          </div>
          <span className="text-xs text-slate-400">{submissions.length} tadan {(currentPage - 1) * itemsPerPage + 1}—{Math.min(currentPage * itemsPerPage, submissions.length)}</span>
        </div>
      )}

      {/* File Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={closePreview} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col border border-slate-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                </div>
                <span className="text-sm font-medium text-slate-900 truncate">{previewFile.name}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <a href={previewFile.downloadUrl} download={previewFile.name}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  Yuklab olish
                </a>
                <button onClick={closePreview}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-3 min-h-[60vh] bg-slate-50 rounded-b-2xl">
              {previewFile.type === 'image' ? (
                <div className="flex items-center justify-center h-full min-h-[60vh]">
                  <img src={previewFile.previewUrl} alt={previewFile.name} className="max-w-full max-h-[75vh] rounded-lg" />
                </div>
              ) : previewFile.type === 'pdf' ? (
                <iframe src={previewFile.previewUrl} className="w-full h-full min-h-[60vh] rounded-lg border-0" title="PDF preview" />
              ) : previewFile.type === 'docx' && previewFile.docxHtml ? (
                <div className="bg-white rounded-lg p-8 max-w-4xl mx-auto min-h-[60vh] prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: previewFile.docxHtml }} />
              ) : (
                <iframe src={previewFile.previewUrl} className="w-full h-full min-h-[60vh] rounded-lg border-0" title="File preview" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Issue Modal */}
      <Modal isOpen={showEditIssueModal} onClose={() => setShowEditIssueModal(false)} title="Sonni tahrirlash">
        <form onSubmit={handleEditIssue} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Nomi</label>
            <input value={editIssueForm.title || ''} onChange={(e) => setEditIssueForm({...editIssueForm, title: e.target.value})} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Tom (Volume)</label>
              <input value={editIssueForm.volume || ''} onChange={(e) => setEditIssueForm({...editIssueForm, volume: e.target.value})} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Son</label>
              <input value={editIssueForm.number || ''} onChange={(e) => setEditIssueForm({...editIssueForm, number: e.target.value})} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Yil</label>
              <input type="number" value={editIssueForm.year || ''} onChange={(e) => setEditIssueForm({...editIssueForm, year: parseInt(e.target.value) || ''})} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShowEditIssueModal(false)} className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm hover:bg-slate-50 transition-colors">Bekor</button>
            <button type="submit" className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors">Saqlash</button>
          </div>
        </form>
      </Modal>

      {/* Manual Article Creation Modal */}
      <Modal isOpen={showArticleModal} onClose={() => setShowArticleModal(false)} title={`Yangi maqola — ${selectedIssue?.title || ''}`}>
        <form onSubmit={handleArticleSubmit} className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
          {/* MAIN AUTHOR */}
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">1. Asosiy muallif <span className="text-rose-500">*</span></h4>
              <button
                type="button"
                onClick={() => setAuthorPicker({ role: 'author' })}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium"
              >
                {articleForm.selectedAuthor ? 'Muallifni o\'zgartirish' : '+ Muallif qo\'shish'}
              </button>
            </div>
            {articleForm.selectedAuthor ? (
              <div className="flex items-center gap-3 bg-white rounded-lg border border-slate-200 px-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {authorLabel(articleForm.selectedAuthor)}
                  </div>
                  {articleForm.selectedAuthor.author?.affiliation && (
                    <div className="text-xs text-slate-500 truncate">
                      {articleForm.selectedAuthor.author.affiliation}
                    </div>
                  )}
                  {articleForm.selectedAuthor.author?.orcid && (
                    <div className="text-xs text-blue-600">
                      ORCID: {articleForm.selectedAuthor.author.orcid}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setArticleField('selectedAuthor', null)}
                  className="text-rose-500 hover:text-rose-700 text-sm"
                >
                  ✕
                </button>
              </div>
            ) : (
              <p className="text-xs text-slate-400">
                "Muallif qo'shish" tugmasini bosib, ORCID orqali yoki bazadan tanlang.
              </p>
            )}
          </div>

          {/* ARTICLE META */}
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
            <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">2. Maqola ma'lumotlari</h4>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Til <span className="text-rose-500">*</span></label>
                <select required value={articleForm.language} onChange={(e) => setArticleField('language', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm">
                  <option value="uz">O'zbek</option>
                  <option value="ru">Rus</option>
                  <option value="en">Ingliz</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">Bo'lim / yo'nalish <span className="text-rose-500">*</span></label>
                {articleForm.sectionCustom ? (
                  <div className="flex gap-2">
                    <input
                      required
                      value={articleForm.section}
                      onChange={(e) => setArticleField('section', e.target.value)}
                      placeholder="01.00.00 — Fizika-matematika fanlari"
                      className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setArticleForm(prev => ({ ...prev, sectionCustom: false, section: '' }))}
                      className="px-3 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-lg border border-slate-200"
                    >
                      Ro'yxatga
                    </button>
                  </div>
                ) : (
                  <select
                    required
                    value={articleForm.section}
                    onChange={(e) => {
                      if (e.target.value === '__custom__') {
                        setArticleForm(prev => ({ ...prev, sectionCustom: true, section: '' }));
                      } else {
                        setArticleField('section', e.target.value);
                      }
                    }}
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                  >
                    <option value="">— Bo'limni tanlang —</option>
                    {sections.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                    <option value="__custom__">+ Yangi bo'lim kiritish…</option>
                  </select>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Sarlavha (UZ) <span className="text-rose-500">*</span></label>
                <input required value={articleForm.title} onChange={(e) => setArticleField('title', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Title (EN) <span className="text-rose-500">*</span></label>
                <input required value={articleForm.title_en} onChange={(e) => setArticleField('title_en', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Заголовок (RU)</label>
                <input value={articleForm.title_ru} onChange={(e) => setArticleField('title_ru', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm" />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Annotatsiya (UZ) <span className="text-rose-500">*</span></label>
                <textarea required rows={3} value={articleForm.abstract} onChange={(e) => setArticleField('abstract', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Abstract (EN) <span className="text-rose-500">*</span></label>
                <textarea required rows={3} value={articleForm.abstract_en} onChange={(e) => setArticleField('abstract_en', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Аннотация (RU)</label>
                <textarea rows={3} value={articleForm.abstract_ru} onChange={(e) => setArticleField('abstract_ru', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Kalit so'zlar (UZ) <span className="text-rose-500">*</span></label>
                  <input required value={articleForm.keywords} onChange={(e) => setArticleField('keywords', e.target.value)} placeholder="vergul bilan ajrating" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Keywords (EN) <span className="text-rose-500">*</span></label>
                  <input required value={articleForm.keywords_en} onChange={(e) => setArticleField('keywords_en', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Ключевые слова (RU)</label>
                  <input value={articleForm.keywords_ru} onChange={(e) => setArticleField('keywords_ru', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Adabiyotlar ro'yxati</label>
                <textarea rows={3} value={articleForm.references_list} onChange={(e) => setArticleField('references_list', e.target.value)} placeholder="Har bir manba yangi qatorda" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm" />
              </div>
            </div>
          </div>

          {/* CO-AUTHORS */}
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">3. Hammualliflar (ixtiyoriy)</h4>
              <button
                type="button"
                onClick={() => setAuthorPicker({ role: 'coauthor' })}
                className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs hover:bg-slate-100"
              >
                + Hammuallif qo'shish
              </button>
            </div>
            {articleForm.coauthors.length === 0 ? (
              <p className="text-xs text-slate-400">Hammualliflar qo'shilmagan</p>
            ) : (
              <div className="space-y-2">
                {articleForm.coauthors.map((c, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-slate-200">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{authorLabel(c)}</div>
                      {c.author?.affiliation && (
                        <div className="text-xs text-slate-500 truncate">{c.author.affiliation}</div>
                      )}
                      {c.author?.orcid && (
                        <div className="text-xs text-blue-600">ORCID: {c.author.orcid}</div>
                      )}
                    </div>
                    <button type="button" onClick={() => removeCoauthor(idx)} className="text-rose-500 hover:text-rose-700 text-sm">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* FILE */}
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
            <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">4. Maqola fayli <span className="text-rose-500">*</span></h4>
            <input type="file" accept=".docx,.doc,.tex" onChange={handleArticleFileChange} className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
            {articleUploading && <p className="text-xs text-blue-600 mt-2">Yuklanmoqda…</p>}
            {articleForm.file_name && <p className="text-xs text-emerald-700 mt-2">✓ {articleForm.file_name}</p>}
          </div>

          {/* STATUS */}
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
            <label className="block text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">Boshlang'ich status</label>
            <select value={articleForm.status} onChange={(e) => setArticleField('status', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm">
              <option value="submitted">Yuborilgan</option>
              <option value="under_review">Ko'rib chiqilmoqda</option>
              <option value="accepted">Qabul qilingan</option>
              <option value="published">Chop etilgan (backfill)</option>
            </select>
            <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
              Eslatma: <b>Chop etilgan</b> tanlansa — DOI tayinlash va PDF generatsiyasi avtomatik ishlamaydi.
              Maqola alohida “Nashr” oqimi orqali yakunlanishi kerak.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-slate-200 sticky bottom-0 bg-white pb-1">
            <button type="button" onClick={() => setShowArticleModal(false)} className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm hover:bg-slate-50">Bekor</button>
            <button type="submit" disabled={articleSubmitting || articleUploading} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold">
              {articleSubmitting ? 'Saqlanmoqda…' : 'Maqolani yaratish'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Author picker — shared across main author and co-authors */}
      <AuthorPickerModal
        isOpen={!!authorPicker}
        role={authorPicker?.role || 'author'}
        existingAuthors={articleAuthors}
        onClose={() => setAuthorPicker(null)}
        onSelect={handleAuthorPicked}
      />
    </div>
  );
}
